import { Card, CardContent } from '@/components/ui/card'
import { t } from 'i18next'
import { cn } from '@/lib/utils'
import Boss from '../../images/boss.svg?react'
import React from 'react'

interface MainScreenProps {
  footer: React.ReactElement
  isRolePlay?: boolean
  isSpeaking?: boolean
  isConnecting?: boolean
  showWelcomePanel?: boolean
  overlayControls?: React.ReactNode
  avatarImage?: string
}

const MainScreen = ({ footer, isRolePlay = false, isSpeaking = false, isConnecting = false, showWelcomePanel = !isRolePlay, overlayControls, avatarImage }: MainScreenProps) => {
  return (
    <Card className="flex flex-col h-full md:flex-1 md:max-h-full min-h-0 border-0 shadow-xl py-2 gap-2 overflow-hidden">
      <CardContent className="flex-1 min-h-0 overflow-y-auto p-4 py-2 mb-0 flex flex-col space-y-4">
        <div
          className="w-full h-full relative m-0 flex-1 min-h-0 rounded-xl overflow-hidden"
          style={{
            backgroundImage: `url(/chat_bg.png)`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
          }}
        >
          {!showWelcomePanel && (
            <div className="absolute left-4 top-4 flex flex-col gap-2">
              <span className="text-base sm:text-lg font-semibold text-[var(--color-primary)]">{t(isRolePlay ? 'main.roleplay.title' : 'main.chat.title')}</span>
              {isConnecting && (
                <div className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-medium text-white drop-shadow-md">
                  {t(isRolePlay ? 'main.roleplay.connecting' : 'main.chat.connecting')}
                </div>
              )}
            </div>
          )}
          <div
            className={cn('absolute rounded-full bg-secondary flex justify-center items-center transition-all', isSpeaking && 'ring-5 ring-accent animate-pulse')}
            style={{
              width: 180,
              height: 180,
              left: 'calc(50% - 90px)',
              top: 'calc(50% - 90px)',
              overflow: 'hidden',
            }}
          >
            <img src={isRolePlay ? avatarImage || '/boss.png' : '/georgia.png'} alt="Video Chat Placeholder" className="h-40 object-cover mt-6" />
          </div>
          {showWelcomePanel && (
            <div
              className={cn(
                'absolute left-1/2 top-1/2 flex flex-col-reverse lg:flex-row w-[90%] lg:w-[65%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-2xl transition-all duration-300 lg:h-[60%] p-4 gap-4 lg:p-8 lg:gap-8 border border-gray-100/50 backdrop-blur-sm hover:shadow-3xl',
                isSpeaking && 'ring-4 ring-accent/30 ring-offset-2 animate-pulse',
              )}
              style={{
                boxShadow: '0 20px 60px -15px rgba(0, 77, 67, 0.15), 0 10px 30px -10px rgba(0, 77, 67, 0.1)',
              }}
            >
              <div className="flex aspect-square w-[min(12rem,28vw)] shrink-0 items-center justify-center self-center overflow-hidden rounded-2xl lg:aspect-auto lg:h-full lg:w-auto lg:rounded-xl bg-gradient-to-br from-[#E3FFAB]/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#E3FFAB]/10 shadow-inner">
                <img
                  src="/georgia-arms-open.png"
                  alt="Georgia"
                  className="h-full w-full overflow-visible object-cover object-top origin-top scale-90 mix-blend-multiply lg:h-full lg:w-auto lg:object-contain lg:object-center lg:scale-100 transition-transform duration-300 hover:scale-95"
                />
              </div>
              <div className="flex flex-1 flex-col justify-center gap-4 text-left flex-grow">
                <p className="font-bold text-lg lg:text-xl leading-relaxed text-gray-800">{t('main.chat.greeting')}</p>
                <p className="font-medium text-sm lg:text-base leading-relaxed text-gray-600">{t('main.chat.welcome_message')}</p>
              </div>
            </div>
          )}
          {overlayControls && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center p-4">
              <div className="pointer-events-auto max-w-full">{overlayControls}</div>
            </div>
          )}
        </div>
      </CardContent>
      {footer}
    </Card>
  )
}

export default MainScreen
